// Hook that reads the billing state from the platform subscription query cache.
// Used by RootLayout to display the payment warning banner without triggering
// an extra network request (the cache is populated when SubscriptionPage is visited).
//
// Returns null when:
//   - The current user is not a tenant admin (non-admins never see billing banners)
//   - No cache entry exists yet (page not yet visited)
//   - The subscription has no billing block (admin-provisioned tenant)

import { useQueryClient } from '@tanstack/react-query'
import { useCurrentUserOrNull } from '@/lib/hooks/use-current-user'
import { useTenant } from '@/components/shared/TenantShell'
import { queryKeys } from '@/lib/api/queryKeys'
import type { SubscriptionInfo, BillingState } from '@/lib/api/platform'

/**
 * Returns the billing state from the TanStack Query cache, or null.
 * Does NOT trigger a network request — reads cached data only.
 * Only returns data for tenant admins.
 */
export function useSubscriptionStatus(): BillingState | null {
  const me = useCurrentUserOrNull()
  const { slug } = useTenant()
  const queryClient = useQueryClient()

  // Non-admins never see the billing banner
  if (!me?.is_tenant_admin) return null

  const data = queryClient.getQueryData<SubscriptionInfo>(
    queryKeys.platform.subscription(slug),
  )

  return data?.billing ?? null
}
